import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";

const TripContext = createContext(null);
const STORAGE_KEY = "rahhal.trip.v1";

export function TripProvider({ children }) {
  const [stops, setStops] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      console.warn("trip storage read failed", err);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stops));
  }, [stops]);

  const addStop = useCallback((destination, opts = {}) => {
    setStops((s) => {
      if (s.some((x) => x.destination.id === destination.id)) {
        toast.info("هذه الوجهة موجودة في رحلتك بالفعل");
        return s;
      }
      const day = s.length + 1;
      const next = [...s, {
        destination,
        day,
        hotel_tier: opts.hotel_tier || "mid",
        meals_tier: opts.meals_tier || "mid",
        nights: opts.nights || 2,
        include_ticket: opts.include_ticket !== false,
      }];
      toast.success(`✓ ${destination.city_ar || destination.city_en} أُضيف إلى رحلتك`);
      return next;
    });
  }, []);

  const removeStop = useCallback((destId) => {
    setStops((s) => s.filter((x) => x.destination.id !== destId).map((x, i) => ({ ...x, day: i + 1 })));
  }, []);

  const updateStop = useCallback((destId, patch) => {
    setStops((s) => s.map((x) => x.destination.id === destId ? { ...x, ...patch } : x));
  }, []);

  const clearTrip = useCallback(() => setStops([]), []);

  const totalUsd = useMemo(() => {
    return stops.reduce((sum, stop) => {
      const p = stop.destination.prices;
      const hotel = p[`hotel_${stop.hotel_tier}_usd`] || 0;
      const meals = (p[`meal_${stop.meals_tier}_usd`] || 0) * 3;
      const transport = p.transport_daily_usd || 0;
      const ticket = stop.include_ticket ? (p.ticket_usd || 0) : 0;
      const perDay = hotel + meals + transport;
      return sum + perDay * stop.nights + ticket;
    }, 0);
  }, [stops]);

  const totalDays = useMemo(() => stops.reduce((s, x) => s + x.nights, 0), [stops]);

  const value = useMemo(
    () => ({ stops, addStop, removeStop, updateStop, clearTrip, totalUsd, totalDays }),
    [stops, addStop, removeStop, updateStop, clearTrip, totalUsd, totalDays]
  );

  return (
    <TripContext.Provider value={value}>
      {children}
    </TripContext.Provider>
  );
}

export const useTrip = () => useContext(TripContext);
